import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Company, CompanyUser } from '@/types';
import { useCompanyMutations } from '@features/admin/data';
import { api } from '@/services/api';
import { showAlertModal, showConfirmModal } from '@shared/components/common/AlertModal';
import { Pagination } from '@shared/components/common/Pagination';
import { validateEmail } from '@/lib/validators';
import { normalizeErrorMessage } from '@/lib/errorUtils';
import {
  Users,
  UserPlus,
  Edit2,
  Key,
  ShieldCheck,
  UserX,
  Check,
  Trash2,
  Loader2,
  AlertTriangle,
  X
} from 'lucide-react';

interface CompanyTeamPanelProps {
  company: Company;
  canEdit: boolean;
  onCompanyUpdated: (updated: Company) => void;
  onClose: () => void;
}

export const CompanyTeamPanel: React.FC<CompanyTeamPanelProps> = ({
  company,
  canEdit,
  onCompanyUpdated,
  onClose
}) => {
  const companyMutations = useCompanyMutations();

  const [teamMembers, setTeamMembers] = useState<CompanyUser[]>([]);
  const [teamPage, setTeamPage] = useState(1);
  const [teamPageSize, setTeamPageSize] = useState(25);
  const [teamTotalCount, setTeamTotalCount] = useState(0);
  const [isLoadingTeam, setIsLoadingTeam] = useState(false);
  const [teamError, setTeamError] = useState('');

  const [isAddingTeamMember, setIsAddingTeamMember] = useState(false);
  const [newMember, setNewMember] = useState({ name: '', email: '', receiveNotifications: true });
  const [memberError, setMemberError] = useState('');
  const [isSavingMember, setIsSavingMember] = useState(false);

  const [editingTeamMember, setEditingTeamMember] = useState<CompanyUser | null>(null);
  const [teamEditError, setTeamEditError] = useState('');
  const [isSavingTeamEdit, setIsSavingTeamEdit] = useState(false);

  const [updatingMemberStatusId, setUpdatingMemberStatusId] = useState<string | null>(null);
  const [deletingMemberId, setDeletingMemberId] = useState<string | null>(null);
  const [settingPrimaryMemberId, setSettingPrimaryMemberId] = useState<string | null>(null);
  const [resettingMemberId, setResettingMemberId] = useState<string | null>(null);
  const [canRenderPortal, setCanRenderPortal] = useState(false);

  // Enable portal after mount
  useEffect(() => {
    setCanRenderPortal(true);
  }, []);

  // Reset team UI when company changes
  useEffect(() => {
    if (company.id) {
      setTeamPage(1);
      setIsAddingTeamMember(false);
      setMemberError('');
      setNewMember({ name: '', email: '', receiveNotifications: true });
      setEditingTeamMember(null);
      setTeamEditError('');
      setUpdatingMemberStatusId(null);
      setSettingPrimaryMemberId(null);
      return;
    }
    setTeamMembers([]);
    setTeamTotalCount(0);
    setTeamError('');
  }, [company.id]);

  // Fetch team members
  useEffect(() => {
    if (!company.id) return;
    let isActive = true;
    setIsLoadingTeam(true);
    setTeamError('');
    api
      .listCompanyTeam(company.id, { page: teamPage, pageSize: teamPageSize })
      .then((data) => {
        if (!isActive) return;
        setTeamMembers(data.items || []);
        setTeamTotalCount(Number(data.totalCount || 0));
      })
      .catch((err: unknown) => {
        if (!isActive) return;
        setTeamMembers([]);
        setTeamTotalCount(0);
        setTeamError(normalizeErrorMessage(err, 'Failed to load company team.'));
      })
      .finally(() => {
        if (isActive) setIsLoadingTeam(false);
      });
    return () => {
      isActive = false;
    };
  }, [company.id, teamPage, teamPageSize]);

  // Clear edit error when editing member changes
  useEffect(() => {
    setTeamEditError('');
  }, [editingTeamMember?.id]);

  const refreshTeamMembers = async () => {
    if (!company.id) return;
    setIsLoadingTeam(true);
    setTeamError('');
    try {
      const data = await api.listCompanyTeam(company.id, {
        page: teamPage,
        pageSize: teamPageSize
      });
      setTeamMembers(data.items || []);
      setTeamTotalCount(Number(data.totalCount || 0));
    } catch (err: unknown) {
      setTeamMembers([]);
      setTeamTotalCount(0);
      setTeamError(normalizeErrorMessage(err, 'Failed to load company team.'));
    } finally {
      setIsLoadingTeam(false);
    }
  };

  const handleAddTeamMember = async () => {
    if (!company.id) return;
    const name = newMember.name.trim();
    const email = newMember.email.trim().toLowerCase();
    if (!name || !email) {
      setMemberError('Please provide both name and email.');
      return;
    }
    setIsSavingMember(true);
    setMemberError('');
    try {
      await api.createCompanyMember(company.id, {
        name,
        email,
        receiveNotifications: newMember.receiveNotifications
      });
      showAlertModal('Team member added! A temporary password has been emailed to them.');
      setIsAddingTeamMember(false);
      setNewMember({ name: '', email: '', receiveNotifications: true });
      await refreshTeamMembers();
      companyMutations.invalidate();
    } catch (e: unknown) {
      setMemberError(normalizeErrorMessage(e, 'Failed to add team member.'));
    } finally {
      setIsSavingMember(false);
    }
  };

  const handleSaveTeamMember = async () => {
    if (!editingTeamMember) return;
    const name = editingTeamMember.name.trim();
    const email = editingTeamMember.email.trim().toLowerCase();
    if (!name || !email) {
      setTeamEditError('Name and email are required.');
      return;
    }
    if (!validateEmail(email).valid) {
      setTeamEditError('Please enter a valid email address.');
      return;
    }
    setIsSavingTeamEdit(true);
    setTeamEditError('');
    try {
      await api.updateCompanyMember(editingTeamMember.id, {
        name,
        email,
        receiveNotifications: editingTeamMember.receiveNotifications
      });
      await refreshTeamMembers();
      companyMutations.invalidate();
      setEditingTeamMember(null);
    } catch (e: unknown) {
      setTeamEditError(normalizeErrorMessage(e, 'Failed to update team member.'));
    } finally {
      setIsSavingTeamEdit(false);
    }
  };

  const handleResetTeamMemberPassword = async (member: CompanyUser) => {
    if (member.isActive === false) {
      showAlertModal('Inactive users must be reactivated before sending password resets.');
      return;
    }
    const confirmed = await showConfirmModal(
      `Send a password reset email to ${member.name} (${member.email})?`,
      { title: 'Send Password Reset', confirmText: 'Send', cancelText: 'Cancel' }
    );
    if (!confirmed) return;
    setResettingMemberId(member.id);
    try {
      await api.resetCompanyMemberPassword(member.id);
      showAlertModal('Reset email sent.');
    } catch (e: unknown) {
      showAlertModal(normalizeErrorMessage(e, 'Failed to send reset email.'));
    } finally {
      setResettingMemberId(null);
    }
  };

  const handleSetTeamMemberPrimary = async (member: CompanyUser) => {
    if (!company.id) return;
    if (member.isActive === false) {
      showAlertModal('Only active users can become the primary contact.');
      return;
    }
    const confirmed = await showConfirmModal(
      `Make ${member.name} (${member.email}) the primary contact for ${company.name}? You can deactivate the previous primary separately if needed.`,
      { title: 'Set Primary Contact', confirmText: 'Set Primary', cancelText: 'Cancel' }
    );
    if (!confirmed) return;
    setSettingPrimaryMemberId(member.id);
    try {
      const result = await api.setCompanyMemberPrimary(company.id, member.id);
      if (result.company) {
        onCompanyUpdated({
          ...company,
          contactPerson: result.company.contactPerson,
          email: result.company.email
        });
      }
      await refreshTeamMembers();
      companyMutations.invalidate();
      showAlertModal(`${member.name} is now the primary contact.`);
    } catch (e: unknown) {
      showAlertModal(normalizeErrorMessage(e, 'Failed to set primary contact.'));
    } finally {
      setSettingPrimaryMemberId(null);
    }
  };

  const handleDeactivateTeamMember = async (member: CompanyUser) => {
    if (!company.id) return;
    const confirmed = await showConfirmModal(
      `Deactivate ${member.name} (${member.email})? They will lose portal access until reactivated.`,
      { title: 'Deactivate User', confirmText: 'Deactivate', cancelText: 'Cancel' }
    );
    if (!confirmed) return;
    setUpdatingMemberStatusId(member.id);
    try {
      await api.deactivateCompanyMember(company.id, member.id);
      await refreshTeamMembers();
      companyMutations.invalidate();
    } catch (e: unknown) {
      showAlertModal(normalizeErrorMessage(e, 'Failed to deactivate team member.'));
    } finally {
      setUpdatingMemberStatusId(null);
    }
  };

  const handleReactivateTeamMember = async (member: CompanyUser) => {
    if (!company.id) return;
    const confirmed = await showConfirmModal(
      `Reactivate ${member.name} (${member.email}) so they can access the portal again?`,
      { title: 'Reactivate User', confirmText: 'Reactivate', cancelText: 'Cancel' }
    );
    if (!confirmed) return;
    setUpdatingMemberStatusId(member.id);
    try {
      await api.reactivateCompanyMember(company.id, member.id);
      await refreshTeamMembers();
      companyMutations.invalidate();
    } catch (e: unknown) {
      showAlertModal(normalizeErrorMessage(e, 'Failed to reactivate team member.'));
    } finally {
      setUpdatingMemberStatusId(null);
    }
  };

  const handleDeleteTeamMember = async (member: CompanyUser) => {
    if (!company.id) return;
    if (member.isPrimary) {
      showAlertModal(
        'You cannot delete the primary contact. Transfer primary to another team member first.'
      );
      return;
    }
    const confirmed = await showConfirmModal(
      `Permanently delete ${member.name} (${member.email})? This removes their login and frees the email for reuse on another company. This cannot be undone.`,
      { title: 'Delete Permanently', confirmText: 'Delete', cancelText: 'Cancel' }
    );
    if (!confirmed) return;
    setDeletingMemberId(member.id);
    try {
      await api.deleteCompanyMember(member.id);
      await refreshTeamMembers();
      companyMutations.invalidate();
    } catch (e: unknown) {
      showAlertModal(normalizeErrorMessage(e, 'Failed to delete team member.'));
    } finally {
      setDeletingMemberId(null);
    }
  };

  return (
    <>
      {/* Team View Modal */}
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden animate-fade-in flex flex-col max-h-[80vh]">
          <div className="p-6 border-b bg-slate-50 flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="bg-purple-100 p-2 rounded-lg text-purple-600">
                <Users className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-bold text-lg text-slate-900">{company.name} Team</h3>
                <p className="text-xs text-slate-500">Secondary users with dashboard access</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {canEdit && (
                <button
                  onClick={() => {
                    setIsAddingTeamMember(true);
                    setMemberError('');
                  }}
                  className="bg-purple-600 text-white px-3 py-1.5 rounded-lg text-xs font-bold hover:bg-purple-700 flex items-center gap-2 shadow-md"
                >
                  <UserPlus className="w-4 h-4" /> Add Team Member
                </button>
              )}
              <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
          <div className="p-6 overflow-y-auto">
            <div className="space-y-3">
              <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 flex justify-between items-center">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center text-slate-500 font-bold uppercase">
                    {company.contactPerson.charAt(0)}
                  </div>
                  <div>
                    <div className="font-bold text-slate-900">{company.contactPerson}</div>
                    <div className="text-sm text-slate-500">{company.email}</div>
                  </div>
                </div>
                <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 border border-slate-200 px-2 py-1 rounded bg-white">
                  Primary Account
                </span>
              </div>

              {isLoadingTeam ? (
                <div className="text-center py-10 border-2 border-dashed border-slate-100 rounded-xl">
                  <Loader2 className="w-5 h-5 animate-spin inline-block mr-2 text-slate-400" />
                  <span className="text-sm text-slate-400 italic">Loading team members...</span>
                </div>
              ) : teamError ? (
                <div className="text-center py-10 border-2 border-dashed border-slate-100 rounded-xl text-red-500">
                  {teamError}
                </div>
              ) : teamMembers.length === 0 ? (
                <div className="text-center py-10 border-2 border-dashed border-slate-100 rounded-xl">
                  <p className="text-sm text-slate-400 italic">No team members found.</p>
                </div>
              ) : (
                [...teamMembers]
                  .sort((left, right) => {
                    const leftActive = left.isActive !== false ? 1 : 0;
                    const rightActive = right.isActive !== false ? 1 : 0;
                    return rightActive - leftActive;
                  })
                  .map((member) => (
                    <div
                      key={member.id}
                      className="bg-white p-4 rounded-xl border border-slate-200 flex justify-between items-center hover:border-purple-200 transition-all group"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-purple-50 rounded-full flex items-center justify-center text-purple-600 font-bold uppercase">
                          {member.name.charAt(0)}
                        </div>
                        <div>
                          <div className="font-bold text-slate-800">{member.name}</div>
                          <div className="text-sm text-slate-500">{member.email}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {member.isActive === false && (
                          <span className="text-[9px] font-bold text-slate-600 bg-slate-100 px-2 py-1 rounded-full">
                            Inactive
                          </span>
                        )}
                        {member.mustChangePassword && (
                          <span className="text-[9px] font-bold text-amber-600 bg-amber-50 px-2 py-1 rounded-full">
                            Password Change Required
                          </span>
                        )}
                        <button
                          onClick={() => setEditingTeamMember({ ...member })}
                          disabled={member.isActive === false}
                          className="p-2 text-slate-300 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-all"
                          title="Edit Team Member"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleResetTeamMemberPassword(member)}
                          disabled={resettingMemberId === member.id || member.isActive === false}
                          className="p-2 text-slate-300 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-all"
                          title="Send Reset Email"
                        >
                          {resettingMemberId === member.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Key className="w-4 h-4" />
                          )}
                        </button>
                        {member.isActive !== false && (
                          <button
                            onClick={() => handleSetTeamMemberPrimary(member)}
                            disabled={settingPrimaryMemberId === member.id}
                            className="p-2 text-slate-300 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all"
                            title="Set as Primary Contact"
                          >
                            {settingPrimaryMemberId === member.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <ShieldCheck className="w-4 h-4" />
                            )}
                          </button>
                        )}
                        {member.isActive !== false ? (
                          <button
                            onClick={() => handleDeactivateTeamMember(member)}
                            disabled={updatingMemberStatusId === member.id}
                            className="p-2 text-slate-300 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-all"
                            title="Deactivate User"
                          >
                            {updatingMemberStatusId === member.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <UserX className="w-4 h-4" />
                            )}
                          </button>
                        ) : (
                          <button
                            onClick={() => handleReactivateTeamMember(member)}
                            disabled={updatingMemberStatusId === member.id}
                            className="p-2 text-slate-300 hover:text-green-600 hover:bg-green-50 rounded-lg transition-all"
                            title="Reactivate User"
                          >
                            {updatingMemberStatusId === member.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Check className="w-4 h-4" />
                            )}
                          </button>
                        )}
                        {!member.isPrimary && (
                          <button
                            onClick={() => handleDeleteTeamMember(member)}
                            disabled={deletingMemberId === member.id}
                            className="p-2 text-slate-300 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                            title="Delete Permanently"
                          >
                            {deletingMemberId === member.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Trash2 className="w-4 h-4" />
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  ))
              )}
            </div>
            <Pagination
              page={teamPage}
              pageSize={teamPageSize}
              totalCount={teamTotalCount}
              onPageChange={setTeamPage}
              onPageSizeChange={(size) => {
                setTeamPageSize(size);
                setTeamPage(1);
              }}
              isLoading={isLoadingTeam}
            />
          </div>
          <div className="p-6 border-t bg-slate-50 flex justify-end">
            <button
              onClick={onClose}
              className="bg-slate-900 text-white px-6 py-2 rounded-xl font-bold hover:bg-slate-800 transition-all"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {/* New Team Member Portal */}
      {isAddingTeamMember && canRenderPortal
        ? createPortal(
            <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[100] p-4">
              <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-fade-in">
                <div className="p-6 bg-white flex justify-between items-center">
                  <h3 className="font-bold text-lg text-slate-800 flex items-center gap-2">
                    <UserPlus className="w-5 h-5 text-purple-600" /> Add Team Member
                  </h3>
                  <button onClick={() => setIsAddingTeamMember(false)}>
                    <X className="w-5 h-5 text-slate-400" />
                  </button>
                </div>
                <div className="p-6 space-y-6">
                  <div className="bg-amber-50 border border-amber-100 p-4 rounded-xl">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                      <div className="text-sm text-amber-800">
                        <p className="font-bold">Important Security Warning</p>
                        <p className="mt-1">
                          You are granting this person <strong>full dashboard access</strong> for{' '}
                          {company.name}.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-500 uppercase mb-1">
                        Full Name
                      </label>
                      <input
                        className="w-full border rounded-xl p-3 bg-slate-50 outline-none focus:ring-2 focus:ring-purple-500"
                        placeholder="Team Member Name"
                        value={newMember.name}
                        onChange={(e) => setNewMember({ ...newMember, name: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-500 uppercase mb-1">
                        Email Address
                      </label>
                      <input
                        type="email"
                        className="w-full border rounded-xl p-3 bg-slate-50 outline-none focus:ring-2 focus:ring-purple-500"
                        placeholder="member@company.com"
                        value={newMember.email}
                        onChange={(e) => setNewMember({ ...newMember, email: e.target.value })}
                      />
                    </div>
                  </div>
                  {memberError && (
                    <div className="text-red-600 text-sm font-medium bg-red-50 p-2 rounded">
                      {memberError}
                    </div>
                  )}
                </div>
                <div className="p-6 border-t bg-slate-50 flex justify-end gap-3">
                  <button
                    onClick={() => setIsAddingTeamMember(false)}
                    className="px-6 py-2 text-slate-600 font-bold"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddTeamMember}
                    disabled={isSavingMember}
                    className="bg-purple-600 text-white px-8 py-2 rounded-xl font-bold hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2 shadow-lg shadow-purple-600/20"
                  >
                    {isSavingMember ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <ShieldCheck className="w-4 h-4" />
                    )}
                    Grant Access
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}

      {/* Edit Team Member Modal */}
      {editingTeamMember && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-fade-in">
            <div className="p-6 border-b bg-slate-50 flex justify-between items-center">
              <h3 className="font-bold text-lg text-slate-800 flex items-center gap-2">
                <Edit2 className="w-5 h-5 text-teal-600" /> Edit Team Member
              </h3>
              <button onClick={() => setEditingTeamMember(null)}>
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">
                  Full Name
                </label>
                <input
                  className="w-full border rounded-xl p-3 bg-slate-50 outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="Team Member Name"
                  value={editingTeamMember.name}
                  onChange={(e) =>
                    setEditingTeamMember({ ...editingTeamMember, name: e.target.value })
                  }
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  className="w-full border rounded-xl p-3 bg-slate-50 outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="member@company.com"
                  value={editingTeamMember.email}
                  onChange={(e) =>
                    setEditingTeamMember({ ...editingTeamMember, email: e.target.value })
                  }
                />
                <p className="mt-1 text-[10px] text-slate-400">
                  Changing email updates the login email for this user.
                </p>
              </div>
              {teamEditError && (
                <div className="text-red-600 text-sm font-medium bg-red-50 p-2 rounded">
                  {teamEditError}
                </div>
              )}
            </div>
            <div className="p-6 border-t bg-slate-50 flex justify-end gap-3">
              <button
                onClick={() => setEditingTeamMember(null)}
                className="px-6 py-2 text-slate-600 font-bold"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveTeamMember}
                disabled={isSavingTeamEdit}
                className="bg-teal-600 text-white px-8 py-2 rounded-xl font-bold hover:bg-teal-700 disabled:opacity-50 flex items-center gap-2 shadow-lg shadow-teal-600/20"
              >
                {isSavingTeamEdit ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <ShieldCheck className="w-4 h-4" />
                )}
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
